import humanize from "humanize-duration";
import * as React from "react";

import { IUser, IUsersAnalytics, TimeTakenChartFilter, UserFilter } from "../../../../App/Analytics/Common";
import { IBrushChartData } from "../../../../formatters/analytics";
import { getTimeTakenGraphData, getValueTotalsFromUsersInAnalytics } from "../../../../parsers/analytics";
import colors from "../../../../styles/colors";
import { humanizeFormat } from "../../../../utils";
import Span from "../../../utils/AnalyticsSpan";
import MoreButton, { More, CloseHelpTextButton } from "../common/MoreButton";
import BrushChart from "./TimeSpentChart";
import {
  SectionContainer,
  SectionTitle,
  StatisticsContainer,
  ChartTextContainer,
  ChartDescriptionContainer,
  ChartContainer,
} from "../common/Charts.styles";
import { TimeTakenIcon } from "../AnalyticsIcons";
import { AnimatedUnmountWrapper } from "../../../utils/AnimationUnmountWrapper/AnimationUnmountWrapper";

interface ITimeSpentGraphProps {
  onRemoveFilter: (name: string, type: UserFilter) => () => void;
  selected: Set<string>;
  tag: string;
  timeTakenChartFilter: TimeTakenChartFilter;
  users: IUser[];
  usersAnalytics: IUsersAnalytics;
}

interface ITimeSpentGraphState {
  graphDomain: [number, number];
  showMore: boolean;
  data: IBrushChartData[];
  maxRange: number;
}

export default class TimeSpentGraph extends React.PureComponent<ITimeSpentGraphProps, ITimeSpentGraphState> {
  constructor(props: ITimeSpentGraphProps) {
    super(props);
    this.state = {
      graphDomain: [0, 0],
      showMore: false,
      ...getTimeTakenGraphData(props.selected, props.usersAnalytics, props.users),
    };
  }

  public componentWillReceiveProps(nextProps: ITimeSpentGraphProps) {
    if (this.props.selected !== nextProps.selected || this.props.usersAnalytics !== nextProps.usersAnalytics) {
      const { users } = this.props;
      this.setState(getTimeTakenGraphData(nextProps.selected, nextProps.usersAnalytics, users));
    }
  }

  public render() {
    const { users, selected, tag, onRemoveFilter } = this.props;
    const { averageValue: allAvg, maxValue: allMax, minValue: allMin } = this.calculateTotals(users);
    const { graphDomain, showMore, data, maxRange } = this.state;
    return (
      <SectionContainer>
        <StatisticsContainer>
          <ChartTextContainer>
            <SectionTitle>
              <TimeTakenIcon /> Time Taken
            </SectionTitle>
            <ChartDescriptionContainer>
              <div>
                Learners took on average{" "}
                <Span color={colors.chartLightBlue100}>
                  {humanize(allAvg, {
                    round: true,
                    ...humanizeFormat(allAvg),
                  })}
                </Span>{" "}
                to complete the {tag}. <br />
                <br />
                The quickest Learners have taken as little as{" "}
                <Span color={colors.chartDarkBlue100}>
                  {humanize(allMin, {
                    round: true,
                    ...humanizeFormat(allMin),
                  })}
                </Span>{" "}
                and the slowest as long as{" "}
                <Span color={colors.chartDarkBlue100}>
                  {humanize(allMax, {
                    round: true,
                    ...humanizeFormat(allMax),
                  })}
                </Span>
                .
              </div>
              <MoreButton onClick={this.onToggleShowMore} isOpenText={this.state.showMore} />
            </ChartDescriptionContainer>
          </ChartTextContainer>
          <ChartContainer className="time-spent">
            <BrushChart
              isFilterActive={selected.size > 0}
              updateChartFilter={this.updateChartFilter}
              onRemoveFilter={onRemoveFilter}
              data={data}
              maxRange={maxRange}
              domain={graphDomain}
            />
          </ChartContainer>
        </StatisticsContainer>
        <AnimatedUnmountWrapper isMounted={showMore} delayTime={300}>
          <More>
            <CloseHelpTextButton onClick={this.onToggleShowMore} isOpenText={this.state.showMore} />
            <p>This chart shows the distribution of time that Learners took to complete this course.</p>
            <p>
              The height of the line represents the number of Learners who took that amount of time. For example, a high
              value indicates that many Learners took that amount of time while a low value indicates fewer Learners
              did. To view specific times, move your cursor over the chart to view the time in a tooltip.
            </p>
            <p>
              To create filters, click and drag a position on the chart. The chart will be filtered to show only the
              Learners who took that amount of time. Time Taken takes into consideration re-attempts at failed
              Post-Assessments and any Lesson content that may need to be reviewed due to a failed Post-Assessment.
            </p>
          </More>
        </AnimatedUnmountWrapper>
      </SectionContainer>
    );
  }

  public onToggleShowMore = () => {
    this.setState({ showMore: !this.state.showMore });
  };

  public updateChartFilter = (minTime: number, maxTime: number) => {
    this.props.timeTakenChartFilter(minTime, maxTime);
  };

  private calculateTotals(users: IUser[]) {
    return getValueTotalsFromUsersInAnalytics(users, this.props.usersAnalytics, "time");
  }
}
